import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

import { PostThemeEnum } from '~/api/blog/blogTypes';
import { BlogsThemeLink, PostLink } from '~/atomic/atom/links/dynamicLinks';
import {
  AntiPirateLink, BlogsLink, RefriverLink,
} from '~/atomic/atom/links/staticLinks';
import { SCTextMedium } from '~/atomic/Typography';
import { openModal } from '~/feature/authorization/authorizationModal.slice';
import { isLoggedInSelector } from '~/feature/user/isLoggedInSelector';
import { isLoggedUserCommercialAuthorSelector } from '~/feature/user/user.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppDispatch, useAppSelector } from '~/store';

import { Accordion } from './Accordion';

export const ImAuthor = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const isLoggedUserCommercialAuthor = useAppSelector(isLoggedUserCommercialAuthorSelector);

  const authorRegisterHandler = () => {
    if (isLoggedIn) {
      if (isLoggedUserCommercialAuthor) {
        router.push('/author/commercial');
        return;
      }
      router.push('/author/registration');
    } else {
      dispatch(openModal('login'));
    }
  };

  return (
    <SCImAuthorPage>
      <SCHeroBlock>
        <SCHeroBlockContent>
          <SCHeroBlockHeaders>
            <SCHeroTitleH1>
              Я - автор
            </SCHeroTitleH1>
            <p>
              <SCSmallText>
                Для успеха в сети современному автору нужно обладать не только ярким
                литературным талантом, но уметь самостоятельно находить читателей и
                взаимодействовать с ними.
              </SCSmallText>
            </p>
            <p>
              <SCSmallText>
                На Bookriver создан набор эффективных инструментов, которые помогут сформировать
                и расширить читательскую аудиторию, упростить труд автора, раскрыть
                его творческий потенциал и повысить продажи.
              </SCSmallText>
            </p>
            <SCWhiteButton
              onClick={authorRegisterHandler}
            >
              Присоединиться
            </SCWhiteButton>
          </SCHeroBlockHeaders>
        </SCHeroBlockContent>
      </SCHeroBlock>
      <SCTools>
        <SCContent>
          <SCStepsRow>
            <SCBlock>
              <SCMediumTitle>
                Инструменты для работы
              </SCMediumTitle>
              <SCFeaturesContainer>
                <SCToolColumn>
                  <SCFeature>
                    <SCFeatureTop>
                      <SCFeatureIcon
                        src="/icon/landing/im-author/coin.svg"
                        alt="иконка монет"
                      />
                      <SCFeatureTitle>
                        Коммерческий статус
                      </SCFeatureTitle>
                    </SCFeatureTop>
                    <SCList>
                      <li>
                        <SCSmallText>
                          Любой автор, в т. ч. проживающий за пределами России, может оформить
                          заявку на
                          {' '}
                          <strong>коммерческий статус за 5 минут</strong>
                          {' '}
                          и легко получить
                          его после проверки данных
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          Коммерческие авторы имеют возможность получать
                          {' '}
                          <strong>
                            каждый день выплаты от продаж
                          </strong>
                          {' '}
                          и вознаграждения читателей на банковскую карту
                        </SCSmallText>
                      </li>
                    </SCList>
                  </SCFeature>
                  <SCFeature>
                    <SCFeatureTop>
                      <SCFeatureIcon
                        src="/icon/landing/im-author/shield-check.svg"
                        alt="иконка щита"
                      />
                      <SCFeatureTitle>
                        Защита от пиратов
                      </SCFeatureTitle>
                    </SCFeatureTop>
                    <SCList>
                      <li>
                        <SCSmallText>
                          <AntiPirateLink>
                            <a>
                              Антипиратский сервис
                            </a>
                          </AntiPirateLink>
                          {' '}
                          позволяет
                          {' '}
                          <strong>
                            отслеживать слив произведений
                          </strong>
                          {' '}
                          на пиратские
                          порталы и блокировать пользователей, которые нелегально
                          используют чужое творчество
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          Автор размечает уникальными маркерами отдельную главу или книгу целиком,
                          чтобы
                          {' '}
                          <strong>идентифицировать покупателя</strong>
                          {' '}
                          и привлечь его к ответственности
                          за нарушение авторских прав
                        </SCSmallText>
                      </li>
                    </SCList>
                  </SCFeature>
                  <SCFeature>
                    <SCFeatureTop>
                      <SCFeatureIcon
                        src="/icon/landing/im-author/new-chart.svg"
                        alt="иконка диаграмм"
                      />
                      <SCFeatureTitle>
                        Аналитика
                      </SCFeatureTitle>
                    </SCFeatureTop>
                    <SCList>
                      <li>
                        <SCSmallText>
                          На площадке Bookriver есть собственная аналитическая система, метрики
                          которой позволяют авторам
                          {' '}
                          <strong>следить за реакцией читателей</strong>
                          {' '}
                          на каждую выпущенную главу или книгу (идет закрытое тестирование)
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          К странице автора или произведения можно также подключить сервис-счетчик
                          Яндекс.Метрика, чтобы дополнительно
                          {' '}
                          <strong>анализировать поведение</strong>
                          {' '}
                          читателей и понять, как они находят книги (скоро)
                        </SCSmallText>
                      </li>
                    </SCList>
                  </SCFeature>
                </SCToolColumn>
                <SCToolColumn>
                  <SCFeature>
                    <SCFeatureTop>
                      <SCFeatureTitle>
                        <SCFeatureIcon
                          src="/icon/landing/im-author/rupor.svg"
                          alt="иконка рупора"
                        />
                        Маркетинг
                      </SCFeatureTitle>
                    </SCFeatureTop>
                    <SCList>
                      <li>

                        <SCSmallText>
                          Инструмент
                          {' '}
                          <PostLink
                            slugPost={`kak-konvertirovat-podpischika-v
                            -chitatelya-marketingovye-instrumenty
                            -ploschadki-bookriver-chast-i-nakopitelnye-skidki-113`}
                          >
                            <a>Накопительные скидки</a>
                          </PostLink>
                          {' '}
                          помогает
                          {' '}
                          <strong>увеличивать продажи</strong>
                          {' '}
                          и средний чек
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          Авторы могут предоставлять возможность покупать
                          {' '}
                          <strong>книги по главам</strong>
                          {' '}
                          в
                          процессе написания, чтобы конвертировать потенциальных читателей в
                          постоянных
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          На личной странице автор может разместить пиксели ВКонтакте и Яндекс.
                          Аудитории, чтобы
                          {' '}
                          <strong>собирать сведения о читателях</strong>
                          {' '}
                          и искать новых
                          для ретаргетинга (скоро)
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          Мы будем добавлять новые инструменты, чтобы авторы могли создавать
                          интересные акции для работы с читателями и повышать продажи своих книг
                        </SCSmallText>
                      </li>
                    </SCList>
                  </SCFeature>
                  <SCFeature>
                    <SCFeatureTop>
                      <SCFeatureIcon
                        src="/icon/landing/im-author/hands.svg"
                        alt="иконка рукопожатия"
                      />
                      <SCFeatureTitle>
                        Партнерская программа
                      </SCFeatureTitle>
                    </SCFeatureTop>
                    <SCList>
                      <li>
                        <SCSmallText>
                          Программа
                          {' '}
                          <RefriverLink>
                            <a>
                              Refriver
                            </a>
                          </RefriverLink>
                          {' '}
                          позволяет получать дополнительный
                          {' '}
                          <strong>кэшбек до 10%</strong>
                          {' '}
                          то продажи книг
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          Автор может вовлекать в процесс распространения ссылок
                          своих читателей, блогеров, владельцев сайтов, маркетологов,
                          чтобы наращивать аудиторию,
                          {' '}
                          <strong>совместно продвигать произведения</strong>
                          {' '}
                          и совместно на этом зарабатывать
                        </SCSmallText>
                      </li>
                    </SCList>
                  </SCFeature>
                  <SCFeature>
                    <SCFeatureTop>
                      <SCFeatureIcon
                        src="/icon/landing/im-author/conversation.svg"
                        alt="иконка сообщений"
                      />
                      <SCFeatureTitle>
                        Рекомендации и общение
                      </SCFeatureTitle>
                    </SCFeatureTop>
                    <SCList>
                      <li>
                        <SCSmallText>
                          Рекомендательная система
                          {' '}
                          <strong>выявляет интересы читателей</strong>
                          {' '}
                          и перенаправляет их на страницы конкретных авторов и их произведений,
                          автоматически формируя аудиторию (идет закрытое тестирование)
                        </SCSmallText>
                      </li>
                      <li>
                        <SCSmallText>
                          <BlogsLink>
                            <a>
                              Блоги
                            </a>
                          </BlogsLink>
                          {' '}
                          и
                          {' '}
                          <BlogsThemeLink
                            theme={PostThemeEnum.Interview}
                          >
                            <a>
                              селфи-интервью
                            </a>
                          </BlogsThemeLink>
                          {' '}
                          – самый свободный способ для автора рассказать
                          о личном опыте, творческом процессе, успехах и сложностях, книгах и их
                          героях, чтобы
                          {' '}
                          <strong>привлечь дополнительное внимание</strong>
                          {' '}
                          к своему творчеству
                        </SCSmallText>
                      </li>
                    </SCList>
                  </SCFeature>
                </SCToolColumn>
              </SCFeaturesContainer>
            </SCBlock>
          </SCStepsRow>
        </SCContent>
      </SCTools>
      <SCForWritersBackground>
        <SCContent>
          <SCStepsRow>
            <SCBlock>
              <SCMediumTitle>
                Становитесь автором на Bookriver
              </SCMediumTitle>
              <SCTextMedium>
                Чтобы собрать свою читательскую аудторию, сформировать репутацию
                интересного автора
                и максимально зарабатывать от продажи своих книг
              </SCTextMedium>
              <SCWave src="/icon/wave-blue.svg" alt="иконка волны" />
            </SCBlock>
          </SCStepsRow>
          <SCBecomeAuthorRow>
            <SCBecomAuthorContentBlock>
              <SCRegistrationRow>
                <SCBlock>
                  <SCGhostNumber data-is-left="true">1</SCGhostNumber>
                  <SCSmallTitle>
                    Станьте автором на Bookriver
                  </SCSmallTitle>
                  <SCInfoRow>
                    <SCImg
                      src="/icon/landing/im-author/watch.svg"
                      width={20}
                      height={20}
                      alt="иконка часов"
                    />
                    1 минута
                  </SCInfoRow>
                  <SCSmallText>
                    Чтобы зарегистрироваться на Bookriver как автор, достаточно указать имя, дату
                    рождения и номер телефона. Всё! После этого уже можно размещать произведения
                    для бесплатного чтения и получать вознаграждения от благодарных пользователей.
                  </SCSmallText>
                </SCBlock>
              </SCRegistrationRow>
              <SCCommercialStatusRow>
                <SCBlock>
                  <SCGhostNumber data-is-left="true">2</SCGhostNumber>
                  <SCSmallTitle>
                    Получите коммерческий статус
                  </SCSmallTitle>
                  <SCInfoRow>
                    <SCImg
                      src="/icon/landing/im-author/watch.svg"
                      width={20}
                      height={20}
                      alt="иконка часов"
                    />
                    5 минут
                  </SCInfoRow>
                  <SCSmallText>
                    На Bookriver стать коммерческим автором может каждый! Статус нужен, чтобы
                    продавать свои произведения и выводить заработанные средства на банковскую
                    карту. Для оформления понадобятся паспортные данные для заключения договора
                    с площадкой и немного терпения: наши модераторы лично рассматривают каждую
                    заявку, поэтому на обработку может понадобится некоторое время.
                  </SCSmallText>
                </SCBlock>
              </SCCommercialStatusRow>
            </SCBecomAuthorContentBlock>
          </SCBecomeAuthorRow>

          <SCFirstBookRow>
            <SCFirstBookContentBlock>
              <SCPublishRow>
                <SCBlock>
                  <SCGhostNumber data-is-left="true">3</SCGhostNumber>
                  <SCSmallTitle>
                    Опубликуйте первое произведение
                  </SCSmallTitle>
                  <SCInfoRow>
                    <SCImg
                      src="/icon/landing/im-author/watch.svg"
                      width={20}
                      height={20}
                      alt="иконка часов"
                    />
                    5 минут
                  </SCInfoRow>
                  <SCSmallText>
                    Как автор, вы можете размещать на Bookriver свои законченные произведения или
                    выкладывать книгу по главам в процессе написания, чтобы быстро получать
                    обратную связь от читателей. Можно загрузить книгу файлом в формате fb2 или
                    docx или писать текст самому в редакторе. Можно добавлять иллюстрации или не
                    добавлять… В общем, да здравствует полная свобода творчества!
                  </SCSmallText>
                </SCBlock>
              </SCPublishRow>
              <SCGetMoneyRow>
                <SCBlock>
                  <SCGhostNumber data-is-left="true">4</SCGhostNumber>
                  <SCSmallTitle>
                    Выводите средства на банковскую карту
                  </SCSmallTitle>
                  <SCInfoRow>
                    <SCImg
                      src="/icon/landing/im-author/watch.svg"
                      width={20}
                      height={20}
                      alt="иконка часов"
                    />
                    1 раз в день
                  </SCInfoRow>
                  <SCSmallText>
                    Для продажи произведений и вывода заработанных средств нужно привязать карту
                    российского банка или зарубежный счет — сделать это можно при оформлении
                    коммерческого статуса. Минимальная сумма для вывода — 2000 ₽, перечислять
                    средства можно один раз в день.
                  </SCSmallText>
                </SCBlock>
              </SCGetMoneyRow>
            </SCFirstBookContentBlock>
          </SCFirstBookRow>

          <SCRow style={{ justifyContent: 'center' }}>
            <SCButton onClick={authorRegisterHandler}>
              Стать коммерческим автором
            </SCButton>
          </SCRow>

        </SCContent>
      </SCForWritersBackground>

      <SCWhatIs>
        <SCContent>
          <SCRow>
            <SCBlock>
              <SCRow>
                <SCMediumTitle>Важные уточнения</SCMediumTitle>
              </SCRow>
              <SCRow>
                <SCBlock>
                  <SCRow>
                    <SCSmallTitle>Комиссия</SCSmallTitle>
                    <SCCommissionBlock>
                      <SCSmallText>
                        На Bookriver мы нацелены собирать всех самых интересных авторов, будь то
                        признанные мастера или талантливые новички. Поэтому мы создаем самые
                        выгодные и комфортные условия для их работы. Сейчас комиссия
                        площадки составляет:
                      </SCSmallText>
                      <SCList>
                        <li>
                          <SCSmallText>
                            15% на эксклюзивное размещение книги
                          </SCSmallText>
                        </li>
                        <li>
                          <SCSmallText>
                            50% на неэксклюзивное размещение книги
                          </SCSmallText>
                        </li>
                        <li>
                          <SCSmallText>
                            10% на вознаграждения
                          </SCSmallText>
                        </li>
                      </SCList>
                    </SCCommissionBlock>
                  </SCRow>
                  <SCSeparator />
                  <SCRow>
                    <SCSmallTitle>
                      Авторское право
                    </SCSmallTitle>
                    <SCSmallText>
                      При публикации произведения на Bookriver все права остаются у автора.
                      Как распоряжаться ими — его личное дело. Никто не помешает автору
                      размещать произведения на других площадках, издавать в бумаге,
                      продать права или снять кино. Кроме случаев, когда автор размещает
                      книгу эксклюзивно на Bookriver
                    </SCSmallText>
                  </SCRow>
                  <SCSeparator />
                  <SCRow>
                    <SCSmallTitle>
                      Налоги
                    </SCSmallTitle>
                    <SCSmallText>
                      <p>
                        Мы работаем с авторами – физическими лицами, а также зарегистрированными в
                        качестве ИП и самозанятых. Однако Bookriver не являемся налоговым агентом и
                        авторы должны платить налоги самостоятельно, поэтому мы рекомендуем
                        получать статус самозанятого. Он дает возможность легально
                        оптимизировать процесс уплаты налога на профессиональный
                        доход и пользоваться льготной ставкой в 6% вместо 13%.
                      </p>

                      <p>
                        Заманчиво, но кажется сложным? Мы поможем во всем разобраться:
                        {' '}
                        <a
                          href="https://support.bookriver.ru/knowledge_base/item/202393?sid=47565"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          подробная инструкция по оформлению статуса самозанятого тут
                        </a>
                      </p>
                    </SCSmallText>
                  </SCRow>
                </SCBlock>
              </SCRow>
            </SCBlock>
          </SCRow>
        </SCContent>
      </SCWhatIs>

      <SCForReadersBackground>
        <SCContent>
          <SCIdeas>
            <SCRow>
              <SCBlock>
                <SCMediumTitle>Есть идеи, как сделать площадку лучше?</SCMediumTitle>
                <SCTextMedium>
                  Наша сильная IT-команда возьмется за реализацию любых интересных доработок и
                  улучшений
                </SCTextMedium>
                <a
                  href=" https://support.bookriver.ru/ideas/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SCWhiteButton>Поделиться идеями</SCWhiteButton>
                </a>
              </SCBlock>
              <SCBlock>
                <SCMediumTitle>Хотите быть в курсе развития проекта?</SCMediumTitle>
                <SCTextMedium>
                  Подписывайтесь на наш Telegram-канал и сообщества в соцсетях
                </SCTextMedium>
                <SCRow>
                  <a
                    href="https://t.me/bookriverru"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SCImg
                      src="/icon/landing/im-author/tg.svg"
                      width={64}
                      height={64}
                      alt="иконка телеграма"
                    />
                  </a>
                  <a
                    href="https://vk.com/bookriverru"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SCImg
                      src="/icon/landing/im-author/vk.svg"
                      width={64}
                      height={64}
                      alt="иконка вконтакте"
                    />
                  </a>
                </SCRow>
              </SCBlock>
            </SCRow>
          </SCIdeas>
          <SCFAQ>
            <SCRow>
              <SCBigTitle>
                Ответы на часто задаваемые вопросы
              </SCBigTitle>
            </SCRow>
            <SCRow>
              <SCBlock>
                <Accordion
                  title={(
                    <SCMediumTitle>
                      Просто ли стать коммерческим автором?
                    </SCMediumTitle>
                  )}
                  content={(
                    <SCBlock>
                      <SCTextMedium>
                        <p>
                          Да! На Bookriver каждый может стать успешным автором и зарабатывать своим
                          творчеством. Это быстро, просто и абсолютно бесплатно.
                          Узнать всё о коммерческом статусе автора
                        </p>
                        <p>
                          <strong>Коммерческий статус нужен, если автор хочет:</strong>
                          <ol>
                            <li>
                              Продавать свои произведения, законченные или в процессе написания.
                            </li>
                            <li>
                              Получать вознаграждения от читателей.
                            </li>
                            <li>
                              Выводить заработанные средства на банковскую карту или счет.
                            </li>
                          </ol>
                        </p>
                        <SCButton onClick={authorRegisterHandler}>
                          Стать коммерческим автором
                        </SCButton>
                      </SCTextMedium>
                    </SCBlock>
                  )}
                />
                <SCSeparator />
                <Accordion
                  title={(
                    <SCMediumTitle>
                      Может ли некоммерческий автор получать денежные вознаграждения от читателей?
                    </SCMediumTitle>
                  )}
                  content={(
                    <SCBlock>
                      <SCTextMedium>
                        <p>
                          Да, может. Но есть ограничение: на личный счет зачислится не более 4000
                          рублей в год. Эти средства вы сможете потратить на покупку книг или
                          услуг площадке, но их нельзя будет вывести с сайта. Чтобы получить
                          такую возможность, нужно оформить статус коммерческого автора.
                          Узнать всё о коммерческом статусе автора
                        </p>

                        <p>
                          <strong>
                            Важно: на вознаграждения распространяется комиссия 10%. Средства от
                            комиссии идут на погашение издержек по банковским операциям.
                          </strong>
                        </p>
                      </SCTextMedium>
                    </SCBlock>
                  )}
                />
                <SCSeparator />
                <Accordion
                  title={(
                    <SCMediumTitle>
                      Зачем автору статус самозанятого?
                    </SCMediumTitle>
                  )}
                  content={(
                    <SCBlock>
                      <SCTextMedium>
                        <p>
                          Самозанятый — это человек, который платит в РФ налог на профессиональный
                          доход по льготной ставке 6% вместо 13%. При этом ему не нужно
                          оплачивать подоходный налог или налог на прибыль.
                        </p>
                        <p>
                          <strong>
                            Налоговый статус самозанятого дает автору следующие возможности:
                          </strong>
                          <ul>
                            <li>
                              иметь подтвержденный доход без регистрации в качестве ИП или ООО;
                            </li>
                            <li>
                              не бояться штрафов за незаконную предпринимательскую деятельность;
                            </li>
                            <li>
                              экономить и платить налог по выгодной ставке;
                            </li>
                            <li>
                              открыто продавать свои произведения, рассказывать о них и
                              рекламировать.
                            </li>
                          </ul>
                          <p>
                            Оформить статус самозанятого очень быстро и просто — вся процедура
                            займет несколько минут. Лично посещать отделение ФНС не нужно, весь
                            процесс проходит в онлайн-режиме.
                          </p>
                          <a> Получить инструкцию по статусу самозанятого </a>
                        </p>
                      </SCTextMedium>
                    </SCBlock>
                  )}
                />
              </SCBlock>
            </SCRow>
          </SCFAQ>
        </SCContent>
        <SCBecomeAuthor>
          <SCRow>
            <SCBlock>
              <SCBigTitle>Становитесь автором сегодня,</SCBigTitle>
              <SCBigText>
                чтобы зарабатывать своим творчеством
                уже завтра
              </SCBigText>
              <SCButtonYellow onClick={() => {
                dispatch(openModal('login'));
              }}
              >
                Стать автором на Bookriver
              </SCButtonYellow>
            </SCBlock>
          </SCRow>
        </SCBecomeAuthor>
        <SCCurve />
      </SCForReadersBackground>

    </SCImAuthorPage>
  );
};

const SCList = styled.ul`
  margin-top: 24px;

  li {
    position: relative;
    margin-bottom: 22px;
    list-style-image: url("/icon/list-check.svg");

    span {
      display: inline-block;
      position: absolute;
      top: 50%;
      left: 28px;
      transform: translateY(-60%);
    }
  }
`;

const SCFeatureIcon = styled.img`
  width: 48px;
  height: 48px;
  margin-right: 20px;

  ${lessThan('xs')} {
    display: none;
  }
`;

const SCFeatureTitle = styled.h6`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 28px;
  margin-bottom: unset;

  color: var(--black-color);
`;

const SCFeatureTop = styled.div`
  display: flex;
  flex-direction: row;

  ${lessThan('sm')} {
    justify-content: center;
  }
`;

const SCFeature = styled.div`
  padding: 30px;
  background-color: var(--white-color);
  box-shadow: 0 1px 1px rgba(141, 147, 162, 0.25);
  border-radius: 12px;
  margin-bottom: 30px;

  ${SCList} {
    padding: unset;

    li {
      list-style-position: inside;
      max-width: 504px;

      span {
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 26px;
        max-width: 504px;
        color: var(--gray-color);
        position: relative;
        top: unset;
        left: unset;
        margin-left: 68px;
        margin-top: -38px;
        transform: unset;
      }
    }
  }

  ${lessThan('sm')} {
    padding: 20px;

    ${SCList} {
      margin-top: 12px;

      li {
        display: block;

        span {
          margin-left: unset;
          margin-top: unset;
          font-size: 15px;
          line-height: 22px;
          text-align: left;
        }
      }
    }
  }
`;

const SCFeaturesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  ${lessThan('lg')} {
    flex-direction: column;
  }

  ${lessThan('sm')} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const SCToolColumn = styled.div`
  margin-right: auto;
  margin-left: 0;

  &:first-child {
    margin-left: auto;
    margin-right: 36px;
  }

  ${lessThan('lg')} {
    margin-right: auto;
    margin-left: auto;

    &:first-child {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const SCButton = styled.button`
  background: var(--primary-color);
  border-radius: 40px;
  font-family: var(--main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  color: var(--white-color);
  padding: 20px 36px;
  border: unset;
  cursor: pointer;

  &:focus {
    outline: unset;
  }

  &:hover {
    background: var(--primary-hover-color);
  }

  ${lessThan('md')} {
    font-size: 18px;
    line-height: 24px;
    padding: 16px 26px;
  }
`;

const SCWhiteButton = styled(SCButton)`
  background-color: var(--white-color);
  color: var(--primary-color);
  border: 2px solid var(--white-color);
  transition: color 0.2s ease-in-out,
  background-color 0.2s ease-in-out;

  &:hover {
    background-color: transparent;
    color: var(--white-color);
  }

  ${lessThan('sm')} {
    font-size: 14px;
    line-height: 20px;
    padding: 8px 13px;
  }
`;

const SCSmallText = styled.span`
  font-family: var(--main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 26px;
  color: var(--gray-color);

  ${lessThan('md')} {
    text-align: center;
    font-size: 15px;
    line-height: 20px;
  }
`;

const SCCommissionBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const SCHeroTitleH1 = styled.h1`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 40px;
  line-height: 48px;
  margin-bottom: 48px;

  color: var(--white-color);

  ${lessThan('sm')} {
    font-size: 16px;
    line-height: 24px;
    max-width: 264px;
    margin-bottom: 24px;
  }
`;

const SCHeroBlockHeaders = styled.div`
  max-width: 590px;

  ${SCSmallText} {
    color: var(--white-color);
  }

  ${SCWhiteButton} {
    margin-top: 30px;
  }

  ${lessThan('md')} {
    ${SCWhiteButton} {
      margin-top: unset;
    }

    ${SCSmallText} {
      font-size: 13px;
    }
  }
`;

const SCHeroBlockContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 128px 256px;

  ${lessThan('md')} {
    padding: 37px 74px;
  }

  ${lessThan('sm')} {
    padding: 16px 36px;
  }
`;

const SCHeroBlock = styled.section`
  position: relative;
  width: 100%;
  max-width: 1400px;
  background-image: url('/icon/landing/im-author/hero-book.jpg');
  background-size: cover;
  background-position: center;
  margin-left: auto;
  margin-right: auto;
  height: 614px;


  ${lessThan('xl')} {
    padding-top: 44.05%;
    height: unset;
    min-height: 440px;
  }
`;

const SCGhostNumber = styled.span`
  position: absolute;
  right: 0;
  top: 0;
  font-family: var(--landing-main-font);
  font-size: 200px;
  line-height: 48px;
  color: var(--gray3-color);
  opacity: 0.3;

  &[data-is-left="true"] {
    right: 100%;
    transform: translateX(50%);
  }

  ${lessThan('md')} {
    font-size: 150px;
    line-height: 48px;
  }
`;

const SCInfoRow = styled.span`
  font: var(--main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 26px;
  color: var(--black-color);
  margin-bottom: 16px;

  img {
    position: relative;
    top: -2px;
    margin-right: 12px;
  }

  ${lessThan('md')} {

    font-size: 15px;
    line-height: 26px;

    img {
      width: 16px;
      height: 16px;
    }
  }
`;

const SCCurve = styled.div`
  position: absolute;
  bottom: 0;
  left: -2px;
  width: 100%;
  height: 119px;
  background-image: url('/image/curve.svg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top;
`;

const SCScreenContainer = styled.div`
  position: relative;

  img {
    position: absolute;
  }
`;

const SCImg = styled.img`

`;

const SCWave = styled.img`
  ${lessThan('md')} {
    width: 38px;
    height: auto;
  }
`;

const SCStarImg = styled.img`
  width: 20px;
  height: 20px;
  position: absolute;
  left: -72px;
  top: -30px;
`;

const SCButtonYellow = styled(SCButton)`
  color: var(--black-color);
  background-color: var(--yellow-dark-color);

  margin-right: auto;
  margin-left: 0;

  &:hover {
    background-color: transparent;
    border: 2px solid var(--yellow-dark-color);
    color: var(--yellow-dark-color);
    padding-top: 18px;
    padding-bottom: 18px;
    padding-left: 34px;
    padding-right: 34px;
  }

  ${lessThan('md')} {
    width: 264px;
    height: 56px;
    margin-right: auto;
    margin-left: auto;
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;

const SCRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;

  ${lessThan('md')} {
    flex-direction: column;
    align-items: center;
  }
`;

const SCSmallTitle = styled.h4`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 24px;
  margin-bottom: 32px;
  position: relative;

  color: var(--black-color);

  ${lessThan('md')} {
    text-align: center;
    font-size: 16px;
    line-height: 24px;
  }
`;

const SCSubtitle = styled.h4`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 24px;
  margin-left: auto;
  margin-right: auto;

  ${lessThan('md')} {
    text-align: center;
    font-size: 16px;
    line-height: 24px;
  }
`;

const SCBigText = styled.p`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 28px;
  color: var(--black-color);

  ${lessThan('md')} {
    text-align: center;
    font-size: 16px;
    line-height: 24px;
    color: var(--white-color);
  }
`;

const SCMediumTitle = styled.h2`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: normal;
  font-size: 40px;
  line-height: 48px;
  color: var(--black-color);

  ${lessThan('md')} {
    text-align: center;
    font-size: 20px;
    line-height: 48px;
  }
`;

const SCBigTitle = styled.h2`
  font-family: var(--landing-main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
  line-height: 48px;
  color: var(--black-color);

  ${lessThan('md')} {
    text-align: center;
    font-size: 32px;
    line-height: 48px;
    color: var(--white-color);
  }
`;

const SCBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;

  ${lessThan('md')} {
    align-items: center;
  }
`;

const SCImAuthorPage = styled.div`
  strong {
    font-weight: 600;
  }
`;

const SCContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: var(--container-width);
  align-items: center;
  margin-left: auto;
  margin-right: auto;
  padding-left: 100px;
  padding-right: 100px;
  padding-bottom: 126px;

  ${lessThan('md')} {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const SCForWritersBackground = styled.div`
  background-color: #fafdff;

  ${SCContent} {
    padding-bottom: 80px;
  }

  ${lessThan('md')} {
    ${SCContent} {
      padding-bottom: 30px;
    }

    ${SCButton} {
      margin-top: -25px;
      font-size: 16px;
      line-height: 24px;
    }
  }
`;

const SCTools = styled(SCForWritersBackground)`
  padding-top: 80px;

  ${lessThan('sm')} {
    padding-top: 40px;
  }

  ${SCContent} {
    padding-left: unset;
    padding-right: unset;
  }
`;

const SCForReadersBackground = styled.div`
  position: relative;
  background-color: #fafdff;

  ${SCContent} {
    padding-bottom: 28px;
  }

  ${lessThan('md')} {
    ${SCContent} {
      padding-top: 36px;
      padding-bottom: 0;
    }
  }
`;

const SCStepsRow = styled(SCRow)`
  margin-bottom: 72px;

  ${SCBlock} {
    align-items: center;
  }

  ${SCMediumTitle} {
    margin-bottom: 22px;
    text-align: center;
  }

  ${SCTextMedium} {
    margin-bottom: 80px;
    text-align: center;

    max-width: 743px;
  }

  ${SCSubtitle} {
    margin-bottom: 54px;
    color: #70797B;
  }

  ${lessThan('md')} {
    margin-bottom: 32px;

    ${SCBlock} {
      align-items: center;
    }

    ${SCMediumTitle} {
      font-size: 20px;
      line-height: 28px;
      text-align: center;
    }

    ${SCSubtitle} {
      margin-bottom: 54px;
      color: #70797B;
    }
  }
`;

const SCCommercialStatusRow = styled(SCRow)`
  ${SCBlock} {
    max-width: 420px;
  }

  ${SCSmallTitle} {
    text-align: left;
  }

  ${lessThan('md')} {

    ${SCBlock} {
      align-items: flex-start;
    }

    ${SCSmallTitle} {
      text-align: left;
      padding-left: 40px;

      ${SCStarImg} {

      }
    }

    ${SCSmallText} {
      text-align: left;
    }
  }
`;

const SCRegistrationRow = styled(SCRow)`
  margin-bottom: 103px;

  ${SCBlock} {
    max-width: 420px;
  }


  ${lessThan('md')} {
    margin-bottom: 40px;

    ${SCBlock} {
      align-items: flex-start;
    }

    ${SCSmallTitle} {
      text-align: left;
    }

    ${SCSmallText} {
      text-align: left;
    }
  }
`;

const SCBecomAuthorContentBlock = styled(SCBlock)`
  margin-top: auto;
  margin-bottom: auto;
  flex-direction: row;

  div:nth-child(2) {
    margin-left: 78px;
  }

  ${SCSmallTitle} {
    margin-bottom: 16px;
  }

  ${lessThan('md')} {
    flex-direction: column;

    div:nth-child(2) {
      margin-top: 30px;
      margin-left: unset;
    }
  }
`;

const SCFirstBookRow = styled(SCRow)`
  ${SCSmallTitle} {
    margin-bottom: 16px;
    text-align: left;
  }

  ${lessThan('md')} {
    margin-bottom: 125px;

    ${SCScreenContainer} {
      height: 333px;
      align-self: flex-start;
      margin-bottom: 42px;

      img {
        height: 333px;
        width: auto;
      }
    }
  }

  ${lessThan('sm')} {
    margin-bottom: 0;
  }
`;

const SCBecomeAuthorRow = styled(SCRow)`

  ${lessThan('md')} {
    flex-direction: column-reverse;
    margin-bottom: 45px;

    ${SCSmallTitle} {
      text-align: left;
      font-size: 16px;
      line-height: 36px;
    }

    ${SCSmallText} {
      text-align: left;
      font-size: 15px;
      line-height: 22px;
      color: var(--gray-color);
    }

    ${SCScreenContainer} {
      height: 333px;
      align-self: flex-start;
      margin-bottom: 42px;

      img {
        height: 333px;
        width: auto;
      }
    }

    ${SCCommercialStatusRow} {

      ${SCStarImg} {
        left: -2px;
        width: 22px;
        height: 22px;
      }

      ${SCSmallTitle} {
        padding-left: 30px;
        font-size: 16px;
        line-height: 20px;
      }
    }
  }
`;

const SCPublishRow = styled(SCRow)`
  margin-bottom: 78px;

  ${SCBlock} {
    max-width: 420px;
  }

  ${lessThan('md')} {
    margin-bottom: 40px;

    ${SCBlock} {
      align-items: flex-start;
    }

    ${SCSmallTitle} {
      text-align: left;
    }

    ${SCSmallText} {
      text-align: left;
    }
  }

`;

const SCFirstBookContentBlock = styled(SCBlock)`
  margin-top: auto;
  margin-bottom: auto;
  flex-direction: row;

  div:nth-child(2) {
    margin-left: 78px;
  }

  ${lessThan('md')} {
    flex-direction: column;

    div:nth-child(2) {
      margin-top: 30px;
      margin-left: unset;
    }
  }
`;

const SCGetMoneyRow = styled(SCRow)`
  margin-bottom: 84px;

  ${SCBlock} {
    max-width: 420px;
  }

  ${lessThan('md')} {
    margin-bottom: 50px;

    ${SCBlock} {
      align-items: flex-start;
    }

    ${SCSmallTitle} {
      text-align: left;
    }

    ${SCSmallText} {
      text-align: left;
    }
  }
`;

const SCIdeas = styled.div`

  ${SCBlock} {
    padding: 78px 54px;
    background: rgba(0, 56, 72, .8);
    border-radius: 0 0 40px 0;
    background-image: url("/icon/landing/im-author/banner.png");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    align-items: flex-start;
    min-width: 600px;

    ${SCMediumTitle} {
      font-size: 24px;
      line-height: 36px;
      color: var(--white-color);
      margin-bottom: 30px;
    }

    ${SCTextMedium} {
      font-size: 18px;
      line-height: 24px;
      font-weight: 600;
      margin-bottom: 41px;
      color: var(--white-color);
    }

    ${SCRow} {
      justify-content: flex-start;
    }

    ${SCImg} {
      margin-left: 0;
      margin-right: 20px;
    }
  }

  ${SCBlock}:first-child {
    background: var(--primary-hover-color);
    border-radius: 40px 0 0 0;
  }

  ${lessThan('md')} {
    ${SCBlock} {
      padding: 28px;
      align-items: flex-start;
      min-width: 320px;

      ${SCMediumTitle} {
        font-size: 16px;
        line-height: 20px;
        margin-bottom: 20px;
        text-align: left;
      }

      ${SCTextMedium} {
        font-size: 15px;
        line-height: 22px;
        margin-bottom: 24px;
      }

      ${SCRow} {
        flex-direction: row;
        justify-content: center;
      }

      ${SCImg} {
        width: 56px;
        height: 56px;
        margin-left: 0;
        margin-right: 28px;

        &:last-child {
          margin-right: unset;
        }
      }

      ${SCWhiteButton} {
        width: 264px;
      }
    }

    ${SCWhiteButton} {
      margin-bottom: 8px;

      &:hover {
        border: 2px solid var(--white-color);
        padding: 14px 32px;
      }
    }
  }

  ${lessThan('md')} {

    ${SCBlock} {
      min-width: 100vw;
      border-radius: unset;
    }

    ${SCBlock}:first-child {
      min-width: 100vw;
      border-radius: unset;
    }
  }
`;

const SCBecomeAuthor = styled.div`
  padding-top: 108px;
  padding-bottom: 112px;
  background-color: #2D3848;

  ${SCBlock} {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 130px;
    width: auto;
    align-items: center;

    ${SCBigTitle} {
      font-size: 32px;
      line-height: 48px;
      color: var(--white-color);
      margin-bottom: 4px;
    }

    ${SCBigText} {
      font-size: 24px;
      line-height: 28px;
      color: var(--gray-color);
      margin-bottom: 52px;
      text-align: center;
    }

    ${SCButtonYellow} {
      margin-left: auto;
      margin-right: auto;
      font-size: 20px;
      line-height: 24px;
    }
  }

  ${lessThan('md')} {
    padding-top: 42px;
    padding-bottom: 40px;

    ${SCBigTitle} {
      font-size: 16px !important;
      line-height: 20px !important;
      margin-bottom: 6px !important;
    }

    ${SCBigText} {
      font-size: 16px !important;
      line-height: 24px !important;
      margin-bottom: 22px !important;
    }

    ${SCButtonYellow} {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }
`;

const SCSeparator = styled.div`
  width: 100%;
  opacity: 0.5;
  height: 1px;
  background-color: var(--gray3-color);
`;

const SCWhatIs = styled.div`
  background-color: var(--white-color);

  ${SCMediumTitle} {
    margin-bottom: 80px;
  }

  ${SCSmallTitle} {
    font-size: 20px;
    line-height: 28px;
    min-width: 212px;
    margin-right: 60px;
    margin-bottom: unset;
  }

  ${SCSmallText} {
    font-size: 18px;
    line-height: 26px;
    margin-bottom: unset;
    color: var(--black-color);

    strong {
      font-weight: 600;
    }
  }

  ${SCContent} {
    padding-top: 78px;
    padding-bottom: 78px;
  }

  ${SCSeparator} {
    margin-top: 46px;
    margin-bottom: 46px;
  }

  ${lessThan('md')} {

    ${SCContent} {
      padding-top: 35px;
      padding-bottom: 35px;
    }

    ${SCMediumTitle} {
      margin-bottom: 32px;
      font-size: 20px;
      line-height: 28px;
    }

    ${SCSmallTitle} {
      text-align: left;
      font-size: 16px;
      line-height: 20px;
      margin-bottom: 24px;
    }

    ${SCSmallText} {
      text-align: left;
      font-size: 15px;
      line-height: 22px;
    }
  }

  ${lessThan('sm')} {
    ${SCSeparator} {
      margin-top: 30px;
      margin-bottom: 30px;
    }
  }

  ${lessThan('md')} {
    ${SCSmallTitle} {
      text-align: center;
      font-size: 16px;
      line-height: 20px;
      margin-bottom: 24px;
      margin-right: unset;
    }
  }
`;

const SCFAQ = styled.div`
  padding-top: 100px;

  ${SCBigTitle} {
    font-size: 40px;
    line-height: 48px;
    color: var(--black-color);
    font-style: normal;
    font-weight: normal;
    margin-bottom: 22px;
  }

  ${SCMediumTitle} {
    font-style: normal;
    font-weight: normal;
    font-size: 20px;
    line-height: 28px;
    margin-bottom: unset;
  }

  ${SCTextMedium} {
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 26px;
  }

  ${lessThan('md')} {
    padding-top: 52px;

    ${SCBigTitle} {
      font-size: 20px;
      line-height: 28px;
    }

    ${SCMediumTitle} {
      text-align: left;
      font-size: 16px;
      line-height: 20px;
    }

    ${SCTextMedium} {
    }
  }
`;
